import {
  createContext, useCallback, useContext, useMemo, useState, type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import { useWebSocket, type WsMessage } from '../hooks/useWebSocket'

// Live screen online/offline status, pushed over the WS hub's "screen_status"
// event (see backend/app/services/screen_status_broadcast.py) instead of the
// dashboard having to poll GET /screens or GET /screens/{id} to notice a
// transition. Mirrors TaskCenterContext's bridge pattern exactly — a
// dedicated `useWebSocket` connection, mounted only while authenticated.
//
// This holds ONLY what's arrived over the socket since mount; a screen with
// no entry yet just means no transition has been pushed since this tab
// connected, NOT that the screen is in some unknown state — callers should
// keep using their own initial GET-fetched status/last_seen and only overlay
// a live entry when one exists (see `get`'s doc below).
export type ScreenStatusEntry = { status: string; effective_status?: string; last_seen: string | null }

type ScreenStatusValue = {
  /** Live status for one screen since this tab connected, or undefined if no
   *  push has arrived yet — callers should fall back to their own GET-fetched
   *  value in that case, not treat undefined as "offline". */
  get: (screenId: number) => ScreenStatusEntry | undefined
}

const ScreenStatusContext = createContext<ScreenStatusValue | null>(null)

function ScreenStatusWsBridge({ onMessage }: { onMessage: (msg: WsMessage) => void }) {
  useWebSocket(onMessage)
  return null
}

export function ScreenStatusProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [live, setLive] = useState<Map<number, ScreenStatusEntry>>(new Map())

  const onMessage = useCallback((msg: WsMessage) => {
    if (msg.event !== 'screen_status') return
    const data = msg.data as { screen_id?: number; status?: string; effective_status?: string; last_seen?: string | null }
    if (typeof data.screen_id !== 'number' || typeof data.status !== 'string') return
    const screenId = data.screen_id
    const status = data.status
    setLive(prev => {
      const next = new Map(prev)
      next.set(screenId, { status, effective_status: data.effective_status, last_seen: data.last_seen ?? null })
      return next
    })
  }, [])

  // Reset on logout so a different account's tab doesn't start with stale
  // status entries from the previous session.
  const get = useCallback((screenId: number) => (user ? live.get(screenId) : undefined), [live, user])

  const value = useMemo<ScreenStatusValue>(() => ({ get }), [get])

  return (
    <ScreenStatusContext.Provider value={value}>
      {user && <ScreenStatusWsBridge onMessage={onMessage} />}
      {children}
    </ScreenStatusContext.Provider>
  )
}

export function useScreenStatus(): ScreenStatusValue['get'] {
  const ctx = useContext(ScreenStatusContext)
  if (!ctx) throw new Error('useScreenStatus must be used within a ScreenStatusProvider')
  return ctx.get
}
